import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { Modal } from '@/components/Modal'
import { SearchInput } from '@/components/SearchInput'
import { DisplayCurrencyItem } from '@/components/DisplayCurrencyItem'
import { SkeletonListTemplate } from '@/components/SkeletonListTemplate'
import { useGetCurrenciesCatalogs } from '@/domain/currencies'
import { CurrencyCatalogsDTO } from '@/domain/currencies'
import { useUpdateWallet } from '@/domain/wallet'
import { useAppPreferencesStore, useUserStore } from '@/store'
import useOffsets from '@/hooks/useOffsets'

interface SelectDisplayCurrencyModalProps {
  isOpen: boolean
  onClose: () => void
  displayCurrencies?: CurrencyCatalogsDTO[]
}

export const SelectDisplayCurrencyModal: React.FC<
  SelectDisplayCurrencyModalProps
> = ({ isOpen, onClose, displayCurrencies: propDisplayCurrencies = [] }) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState('')

  const { setDisplayCurrency } = useAppPreferencesStore()
  const { wallet } = useUserStore()

  // Подключаем реальный API
  const { mutateAsync: updateWallet } = useUpdateWallet()

  // Используем переданные валюты или загружаем как fallback
  const {
    data: currenciesData,
    isLoading: isLoadingFallback,
    isError: isErrorFallback,
  } = useGetCurrenciesCatalogs(
    {
      is_available: true,
      is_display_currency: true,
    },
    {
      enabled: propDisplayCurrencies.length === 0, // Загружаем только если не переданы валюты
    },
  )

  // Определяем какие валюты использовать и состояние загрузки
  const hasPreloadedCurrencies = propDisplayCurrencies.length > 0
  const isLoading = hasPreloadedCurrencies ? false : isLoadingFallback
  const isError = hasPreloadedCurrencies ? false : isErrorFallback

  // Fallback данные на случай, если API не работает
  const fallbackCurrencies: CurrencyCatalogsDTO[] = [
    {
      id: 1,
      isin: 'USD',
      name: 'US Dollar',
      label: '$',
      precision: 2,
      factor: 100,
      img_url:
        'https://storage.googleapis.com/kem-catalog-stage/currencies/USD.png',
    },
    {
      id: 2,
      isin: 'EUR',
      name: 'Euro',
      label: '€',
      precision: 2,
      factor: 100,
      img_url:
        'https://storage.googleapis.com/kem-catalog-stage/currencies/EUR.png',
    },
    {
      id: 3,
      isin: 'GBP',
      name: 'British Pound',
      label: '£',
      precision: 2,
      factor: 100,
      img_url:
        'https://storage.googleapis.com/kem-catalog-stage/currencies/GBP.png',
    },
    {
      id: 4,
      isin: 'AED',
      name: 'UAE Dirham',
      label: 'AED',
      precision: 2,
      factor: 100,
      img_url:
        'https://storage.googleapis.com/kem-catalog-stage/currencies/AED.png',
    },
    {
      id: 5,
      isin: 'JPY',
      name: 'Japanese Yen',
      label: '¥',
      precision: 2,
      factor: 100,
      img_url:
        'https://storage.googleapis.com/kem-catalog-stage/currencies/JPY.png',
    },
    {
      id: 6,
      isin: 'CAD',
      name: 'Canadian Dollar',
      label: 'C$',
      precision: 2,
      factor: 100,
      img_url:
        'https://storage.googleapis.com/kem-catalog-stage/currencies/CAD.png',
    },
  ]

  // Определяем какие валюты использовать
  const currencies: CurrencyCatalogsDTO[] = hasPreloadedCurrencies
    ? propDisplayCurrencies
    : (currenciesData as any)?.currencies?.length > 0
      ? (currenciesData as any).currencies
      : fallbackCurrencies

  // Фильтрация валют по поиску
  const filteredCurrencies = useMemo(() => {
    if (!searchValue.trim()) return currencies

    const normalizedSearch = searchValue.toLowerCase().trim()
    return currencies.filter((currency: CurrencyCatalogsDTO) => {
      const normalizedName = currency.name.toLowerCase()
      const normalizedCode = currency.isin.toLowerCase()

      return (
        normalizedName.includes(normalizedSearch) ||
        normalizedCode.includes(normalizedSearch)
      )
    })
  }, [currencies, searchValue])

  const handleSelectCurrency = async (currency: CurrencyCatalogsDTO) => {
    try {
      // Сначала обновляем локальное состояние
      setDisplayCurrency(currency)

      // Закрываем модальное окно сразу после выбора валюты
      onClose()

      // Обновляем на сервере в фоне
      if (wallet?.walletId) {
        await updateWallet({
          wallet_id: wallet.walletId,
          display_fiat_currency: currency.isin,
        })
      }
    } catch (error) {
      // Модальное окно уже закрыто, ничего дополнительно не делаем
      console.error('Failed to update display currency on server:', error)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  // const handleSearchClear = () => {
  //   setSearchValue('')
  // }

  const renderContent = () => {
    // Показываем скелеты только при первоначальной загрузке валют, но не при обновлении
    if (isLoading) {
      return <SkeletonListTemplate sizeList={6} />
    }

    if (isError) {
      // Не показываем ошибку в UI, просто используем fallback данные
    }

    if (filteredCurrencies.length === 0) {
      return (
        <BaseView
          data-component="select-display-currency-modal-empty"
          center
          py={40}
        >
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
          >
            {searchValue
              ? `${searchValue} ${t('translation:notFound')}`
              : t('translation:noCurrencies')}
          </BaseText>
        </BaseView>
      )
    }

    return (
      <BaseView data-component="select-display-currency-modal-list" gap={8}>
        {filteredCurrencies.map((currency: CurrencyCatalogsDTO) => (
          <DisplayCurrencyItem
            key={currency.id}
            currency={currency}
            onPress={handleSelectCurrency}
          />
        ))}
      </BaseView>
    )
  }

  const offsets = useOffsets()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('translation:selectDisplayCurrency')}
      enableDynamicSizing={false}
      style={{
        maxHeight: '100vh',
        height: '100vh',
        top: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        paddingTop: offsets.top,
      }}
    >
      <BaseView
        gap={16}
        data-component="select-display-currency-modal"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Search Input */}
        <SearchInput
          onChangeText={handleSearchChange}
          placeholder={t('form:placeholders.search')}
          withoutBorder={true}
        />

        {/* Currency List */}
        <BaseView
          style={{
            flex: 1,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {renderContent()}
        </BaseView>
      </BaseView>
    </Modal>
  )
}
